import { BaseServiceSetup, context, utils } from '@ad-engine/core';
import { isDrawerPlayerApplicable } from './is-applicable';
import { JwpTracker } from './tracker';
import { communicationService, UapLoadStatus } from '@ad-engine/communication';
import { AD_ENGINE_UAP_LOAD_STATUS } from '../../communication/events/events-ad-engine-uap';
import { JWP_ICV_INJECT, JWP_ICV_READY } from '../../communication/events/events-misc';

export const jwpLogGroupName = 'jwp-icv' as const;

interface JwpServiceConfig {
	id: string;
	player: string;
	playlist: string;
	playlistIndex: number;
	slotName: string;
	adPosition?: 'preroll' | 'postroll';
	setupOptions: {
		autostart: boolean;
		allowFullscreen: boolean;
		height: number;
		width: number;
	};
}

const defaultOptions: Partial<JwpServiceConfig> = {
	player: 'medaltv',
	slotName: 'drawer',
	setupOptions: {
		autostart: true,
		allowFullscreen: false,
		height: 162,
		width: 288,
	},
};

export class Jwp extends BaseServiceSetup {
	private tracker: JwpTracker;
	private logoTrackerAdded: boolean;
	constructor() {
		super();

		this.tracker = new JwpTracker();
		this.logoTrackerAdded = false;
	}

	private getConfig(): JwpServiceConfig {
		return {
			...defaultOptions,
			...context.get('services.jwpDrawerPlayer'),
		};
	}

	call() {
		if (!isDrawerPlayerApplicable()) {
			utils.logger(jwpLogGroupName, 'disabled');
			return;
		}

		const config = this.getConfig();
		utils.logger(jwpLogGroupName, 'enabled', config);

		communicationService.on(AD_ENGINE_UAP_LOAD_STATUS, (action: UapLoadStatus) => {
			if (!action.isLoaded && action.adProduct !== 'ruap') {
				this.tracker.track('eligible');
				if (!context.get('services.jwp.latePageInject')) {
					this.loadPlayerScript(config);
				}
			}
		});

		communicationService.on(
			JWP_ICV_INJECT,
			() => {
				this.loadPlayerScript({ ...config, ...context.get('services.jwp.options') });
			},
			false,
		);
	}

	enableFloating() {
		utils.logger(jwpLogGroupName, 'Float enabled - player is floating now');
		window.jwplayer(this.getConfig().slotName).setFloating(true);
	}

	disableFloating() {
		utils.logger(jwpLogGroupName, 'Float disabled - player is anchored');
		window.jwplayer(this.getConfig().slotName).setFloating(false);
	}

	private loadPlayerScript(config: JwpServiceConfig) {
		utils.logger(jwpLogGroupName, 'loading player script', config);
		utils.scriptLoader.loadScript(`https://cdn.jwplayer.com/libraries/${config.id}.js`).then(() => {
			this.tracker.track('init');
			this.setupPlayer(config);
			this.loadAdTag(config.slotName, config.adPosition ?? 'preroll', config.player);
			this.registerKeyEventsTracking(config.slotName);
			communicationService.emit(JWP_ICV_READY);
		});
	}

	private setupPlayer(config: JwpServiceConfig) {
		const playerInstance = window.jwplayer(config.slotName).setup({
			...config.setupOptions,
			nextUpDisplay: false,
			playlist: `https://cdn.jwplayer.com/v2/playlists/${config.playlist}`,
			playlistIndex: config.playlistIndex,
			advertising: {
				autoplayadsmuted: true,
				client: 'googima',
				loadVideoTimeout: 16000,
				maxRedirects: 8,
				requestTimeout: 11500,
				truncateMacros: false,
				vastLoadTimeout: 11000,
				vpaidcontrols: true,
			},
			autoPause: {
				viewability: true,
				pauseAds: true,
			},
		});
		playerInstance.on('error', (error: { code: number }) => {
			// skip error code 220000-229999 (HTML5 playback error) and continue to the next video
			if (error.code > 220_000 && error.code < 230_000) {
				playerInstance.next();
			}
		});
	}

	private loadAdTag(slotName: string, adPosition: 'preroll' | 'postroll', player: string) {
		this.tracker.track('loadAdTag');
		const event = adPosition === 'preroll' ? 'beforePlay' : 'beforeComplete';
		let newMediaId = window.jwplayer(slotName).getPlaylistItem()?.mediaid;
		window.jwplayer(slotName).on(event, () => {
			const currentMediaId: string = window.jwplayer(slotName).getPlaylistItem()?.mediaid;
			if (currentMediaId === newMediaId) {
				return;
			}
			newMediaId = currentMediaId;
			const vast = utils.buildVastUrl(16 / 9, slotName, {
				videoAdUnitId: context.get(`bidders.prebid.wikiaVideo.slots.${slotName}.videoAdUnitId`),
				customParams: context.get(`bidders.prebid.wikiaVideo.slots.${slotName}.customParams`),
				targeting: { player },
			});

			utils.logger(jwpLogGroupName, 'vast', vast);
			window.jwplayer(slotName).loadAdTag(vast);

			document.getElementById(slotName)?.classList.add('jwp-icv');
		});
	}

	private registerKeyEventsTracking(slotName: string) {
		const registerJwpEvents = ['play', 'pause', 'complete', 'adClick', 'adComplete', 'adSkipped'];
		registerJwpEvents.forEach((eventName) => {
			window.jwplayer(slotName).on(eventName, () => {
				this.tracker.track(eventName);
				if (eventName === 'play') {
					this.tryToListenToLogoOnClick();
				}
				utils.logger(jwpLogGroupName, 'event', eventName);
			});
		});

		window.jwplayer(slotName).on('adImpression', (e) => {
			this.tracker.track('adImpression', { line_item_id: e.adId });
			utils.logger(jwpLogGroupName, 'event', 'adImpression');
		});
	}

	private tryToListenToLogoOnClick() {
		const logo = document.getElementsByClassName('jw-logo jw-logo-top-right jw-hide jw-reset')[0];

		if (logo && !this.logoTrackerAdded) {
			utils.logger(jwpLogGroupName, 'logo on click listener added');
			this.logoTrackerAdded = true;
			logo.addEventListener('click', () => {
				this.tracker.track('logoClicked');
			});
		} else {
			utils.logger(jwpLogGroupName, 'logo not found or event listener already added');
		}
	}
}
